import React, { useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useOnClickOutside } from 'usehooks-ts';

import Download from '@/assets/icons/Download';
import GeslotenWissel from '@/assets/icons/GeslotenWissel';
import List from '@/assets/icons/List';
import Megaphone from '@/assets/icons/Megaphone';
import Settings from '@/assets/icons/Settings';
import Upload from '@/assets/icons/Upload';
import { PermissionGate } from '@/auth';
import TamUploadDialog from '@/components/dialogs/beheren/tam/TamUploadDialog';
import PushMeldingDialog from '@/components/dialogs/push-melding/PushMeldingDialog';
import OpstartSolverDialog from '@/components/dialogs/solver/OpstartSolverDialog';
import { useVoorraad } from '@/queries/mutaties/useVoorraad';

import styles from './AdditionalOptions.module.css';

export const AdditionalOptionsDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { tamVoorraad } = useVoorraad();
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  const handleOptionClick = () => {
    setIsOpen(false);
  };
  const [showOpstartSolverDialog, setShowOpstartSolverDialog] = useState(false);
  const [showTamUploadDialog, setShowTamUploadDialog] = useState(false);
  const [showPushMeldingDialog, setShowPushMeldingDialog] = useState(false);

  useOnClickOutside(menuRef as React.RefObject<HTMLElement>, () => setIsOpen(false));

  return (
    <div className={styles.additionalOptionsStyle} ref={menuRef}>
      <button
        className={styles.additionalOptionMenuButtonStyle}
        data-testid='settingsButton'
        onClick={toggleMenu}
        aria-label='Extra opties'
        aria-expanded={isOpen}
      >
        <Settings fill='var(--tractie-color-content-interactive-default)' aria-hidden='true' />
      </button>
      {isOpen && (
        <div className={styles.additionalOptionItemsStyle} data-testid='informationMenu'>
          <PermissionGate permission='beheer.manage'>
            <button
              className={styles.additionalOptionsItemStyle}
              onClick={() => {
                handleOptionClick();
                setShowPushMeldingDialog(true);
              }}
            >
              <Megaphone className={styles.additionalOptionIconStyle} aria-hidden='true' />
              <span>Push-melding</span>
            </button>
          </PermissionGate>
          <PermissionGate anyOf={['beheer.matbeheer', 'lmm.view']}>
            <NavLink
              to='/beheer'
              className={styles.additionalOptionsItemStyle}
              onClick={() => handleOptionClick()}
              rel='noreferrer'
            >
              <List className={styles.additionalOptionIconStyle} aria-hidden='true' />
              <span>Materieelbeheer</span>
            </NavLink>
          </PermissionGate>
          <PermissionGate permission='beheer.solver'>
            <button
              className={styles.additionalOptionsItemStyle}
              onClick={() => {
                handleOptionClick();
                setShowOpstartSolverDialog(true);
              }}
            >
              <GeslotenWissel className={styles.additionalOptionIconStyle} aria-hidden='true' />
              <span>OpstartSolver</span>
            </button>
          </PermissionGate>
          <PermissionGate permission='beheer.tam'>
            <>
              <button
                className={styles.additionalOptionsItemStyle}
                onClick={() => {
                  handleOptionClick();
                  setShowTamUploadDialog(true);
                }}
              >
                <Upload className={styles.additionalOptionIconStyle} aria-hidden='true' />
                <span>Upload TAM-plan</span>
              </button>
              <button
                className={styles.additionalOptionsItemStyle}
                onClick={() => {
                  handleOptionClick();
                  tamVoorraad();
                }}
              >
                <Download className={styles.additionalOptionIconStyle} aria-hidden='true' />
                <span>Download TAM voorraad</span>
              </button>
            </>
          </PermissionGate>
        </div>
      )}
      <PermissionGate permission='beheer.manage'>
        {showPushMeldingDialog && (
          <PushMeldingDialog showDialog={showPushMeldingDialog} handleOpenChange={setShowPushMeldingDialog} />
        )}
      </PermissionGate>
      <PermissionGate permission='beheer.solver'>
        {showOpstartSolverDialog && (
          <OpstartSolverDialog showDialog={showOpstartSolverDialog} handleOpenChange={setShowOpstartSolverDialog} />
        )}
      </PermissionGate>
      <PermissionGate permission='beheer.tam'>
        {showTamUploadDialog && (
          <TamUploadDialog showDialog={showTamUploadDialog} handleOpenChange={setShowTamUploadDialog} />
        )}
      </PermissionGate>
    </div>
  );
};
